import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'

export interface DesktopState {
  kind: string
  control: { mode: 'agent' | 'pending' | 'human'; reason?: string; generation: number }
}

export interface DesktopViewerProps {
  worker: { id: string; name: string }
  desktop: DesktopState | null
  viewer_url: string | null
  viewer_access: 'watch' | 'control' | null
  viewer_generation: number | null
  labels: Record<string, string>
  error?: string | null
}

// Fleet owns control/generation. These props are short-lived viewing grants;
// polling never overwrites a grant, and a generation change hides the old frame.
export function useDesktopViewer(props: DesktopViewerProps) {
  const { worker, desktop, viewer_url, viewer_access, viewer_generation } = props
  const base = `/dashboard/digital_workers/${worker.id}/computer`
  const frame = useRef<HTMLIFrameElement>(null)
  const mutating = useRef(false)
  const polling = useRef(false)
  const attempted = useRef<string | null>(null)
  const [busy, setBusy] = useState(false)
  const mode = desktop?.control.mode
  const access = mode === 'human' ? 'control' : mode === 'agent' ? 'watch' : null
  const generation = desktop?.control.generation
  const current = !!viewer_url && access === viewer_access && generation === viewer_generation
  const post = (url: string, data: Record<string, string> = {}) => {
    mutating.current = true
    setBusy(true)
    router.post(url, data, { preserveScroll: true, onFinish: () => { mutating.current = false; setBusy(false) } })
  }
  const refresh = () => { if (access) post(`${base}/open`, { access }) }
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (mutating.current || polling.current) return
      polling.current = true
      router.reload({ only: ['desktop', 'error'], preserveUrl: true, onFinish: () => { polling.current = false } })
    }, 5000)
    return () => window.clearInterval(timer)
  }, [worker.id])
  useEffect(() => {
    const key = `${worker.id}-${generation}-${access}`
    if (!access || current || busy || attempted.current === key) return
    // Run after the parent Inertia mount has initialized the router.
    const timer = window.setTimeout(() => { attempted.current = key; refresh() }, 0)
    return () => window.clearTimeout(timer)
  }, [worker.id, generation, access, current, busy])
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (!viewer_url || event.origin !== new URL(viewer_url).origin || event.source !== frame.current?.contentWindow) return
      if (event.data?.type === 'desktop-viewer' && event.data.status === 'reconnect' && !mutating.current) refresh()
    }
    window.addEventListener('message', receive)
    return () => window.removeEventListener('message', receive)
  }, [viewer_url, access])
  return { frame, mode, busy, current, access, refresh, change: (control: string) => post(`${base}/control`, { control }) }
}
